import { useEffect, useState, type KeyboardEvent } from "react";

type DraftFieldOptions<TDraft, TShared = TDraft> = {
  value: TShared;
  fromShared?: (value: TShared) => TDraft;
  toShared?: (draft: TDraft) => TShared;
  equals?: (a: TDraft, b: TDraft) => boolean;
  onCommit: (value: TShared) => void | Promise<void>;
};

function defaultFromShared<TDraft, TShared>(value: TShared): TDraft {
  return value as unknown as TDraft;
}

function defaultToShared<TDraft, TShared>(draft: TDraft): TShared {
  return draft as unknown as TShared;
}

export function useDraftField<TDraft, TShared = TDraft>({
  value,
  fromShared = defaultFromShared,
  toShared = defaultToShared,
  equals = Object.is,
  onCommit,
}: DraftFieldOptions<TDraft, TShared>) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TDraft>(() => fromShared(value));

  const sharedDraft = fromShared(value);
  const isDirty = !equals(draft, sharedDraft);

  useEffect(() => {
    if (isEditing) return;
    setDraft((current) =>
      equals(current, sharedDraft) ? current : sharedDraft,
    );
  }, [equals, isEditing, sharedDraft]);

  const start = () => {
    setDraft(fromShared(value));
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(fromShared(value));
    setIsEditing(false);
  };

  const commit = async () => {
    if (isDirty) {
      await onCommit(toShared(draft));
    }
    setIsEditing(false);
  };

  return {
    value,
    draft,
    setDraft,
    isEditing,
    isDirty,
    start,
    cancel,
    commit,
  };
}

export function getDraftFieldKeyDownHandlers(field: {
  commit: () => void | Promise<void>;
  cancel: () => void;
}) {
  return (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void field.commit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      field.cancel();
    }
  };
}
